import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import Layout from '../../components/Layout'
import { useAdminUsers, useUpdateUserRole, useUpdateUserPlan, AdminUser } from '../../api/admin'

// Available plans matching the backend with full limits
const PLANS = [
  { id: 1, name: 'Free', daily: 10, maxLines: 500, overage: null },
  { id: 3, name: 'Pro', daily: 50, maxLines: 2000, overage: 0.03 },
  { id: 4, name: 'Max', daily: 250, maxLines: 2000, overage: 0.03 },
]

// Format number with commas
const formatNumber = (n: number | null) => (n === null ? '∞' : n.toLocaleString())

// Plan tooltip content component
function PlanTooltipContent({ plan }: { plan: typeof PLANS[0] }) {
  return (
    <div className="space-y-1.5 text-xs">
      <div className="font-medium text-text-primary border-b border-border pb-1.5 mb-1.5">
        {plan.name} Plan
      </div>
      <div className="flex justify-between">
        <span className="text-text-secondary">Daily</span>
        <span className="text-text-primary font-mono">{formatNumber(plan.daily)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-text-secondary">Max lines</span>
        <span className="text-text-primary font-mono">{formatNumber(plan.maxLines)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-text-secondary">Overage</span>
        <span className="text-text-primary font-mono">
          {plan.overage === null ? '—' : `$${plan.overage.toFixed(2)}`}
        </span>
      </div>
    </div>
  )
}

// Custom plan selector with hover tooltips
function PlanSelector({
  value,
  onChange,
  disabled,
  username
}: {
  value: number
  onChange: (planId: number) => void
  disabled: boolean
  username: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectedPlan = PLANS.find(p => p.id === value) || PLANS[0]

  // Position dropdown when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left
      })
    }
  }, [isOpen])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center justify-between gap-2 rounded-md border-0 py-1.5 pl-3 pr-3 text-sm bg-noir-tertiary text-text-primary ring-1 ring-inset ring-border focus:ring-2 focus:ring-accent-cyan cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px]"
        aria-label={`Change plan for ${username}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{selectedPlan.name}</span>
        <svg className={`w-4 h-4 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown - fixed position to escape overflow:hidden */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-100 flex items-start"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          {/* Options list */}
          <div className="bg-noir-secondary border border-border rounded-lg shadow-xl py-1">
            {PLANS.map((plan) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => {
                  onChange(plan.id)
                  setIsOpen(false)
                }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
                className={`block w-full text-left px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                  plan.id === value
                    ? 'bg-accent-cyan/20 text-accent-cyan'
                    : 'text-text-primary hover:bg-noir-tertiary'
                }`}
                role="option"
                aria-selected={plan.id === value}
              >
                {plan.name}
              </button>
            ))}
          </div>

          {/* Tooltip panel - shows when hovering an option */}
          {hoveredPlan && (
            <div className="ml-2 bg-noir-secondary border border-border rounded-lg shadow-xl p-3 w-44">
              <PlanTooltipContent plan={PLANS.find(p => p.id === hoveredPlan)!} />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default function Users() {
  const { data, isLoading, error } = useAdminUsers()
  const updateUserRole = useUpdateUserRole()
  const updateUserPlan = useUpdateUserPlan()
  const [confirmDialog, setConfirmDialog] = useState<{
    user: AdminUser
    newRole: string
  } | null>(null)

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && confirmDialog) {
        setConfirmDialog(null)
      }
    }

    if (confirmDialog) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [confirmDialog])

  const handleRoleToggle = (user: AdminUser) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin'
    setConfirmDialog({ user, newRole })
  }

  const handlePlanChange = async (user: AdminUser, planId: number) => {
    try {
      await updateUserPlan.mutateAsync({
        userId: user.github_id,
        planId,
      })
    } catch (err) {
      console.error('Failed to update user plan:', err)
    }
  }

  const confirmRoleChange = async () => {
    if (!confirmDialog) return

    try {
      await updateUserRole.mutateAsync({
        userId: confirmDialog.user.user_id,
        role: confirmDialog.newRole,
      })
      setConfirmDialog(null)
    } catch (err) {
      console.error('Failed to update user role:', err)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div
              className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading users"
            >
              <span className="sr-only">Loading users...</span>
            </div>
            <p className="mt-4 text-sm text-text-secondary">Loading users...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-6">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-verdict-rejected"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-verdict-rejected">Error loading users</h3>
              <p className="mt-2 text-sm text-verdict-rejected">
                {error instanceof Error ? error.message : 'Failed to load users'}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const users = data?.users || []

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-text-primary">User Management</h1>
          <div className="text-sm text-text-secondary">
            Total users: <span className="font-semibold">{data?.total || 0}</span>
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-noir-tertiary">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    Plan
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-noir-secondary divide-y divide-border">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-noir-tertiary transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-text-primary">
                            {user.username}
                          </div>
                          <div className="text-xs text-text-secondary">
                            GitHub ID: {user.github_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-accent-cyan/20 text-accent-cyan'
                            : 'bg-noir-tertiary text-text-secondary'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <PlanSelector
                        value={user.plan_id || 1}
                        onChange={(planId) => handlePlanChange(user, planId)}
                        disabled={updateUserPlan.isPending}
                        username={user.username}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                      {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleRoleToggle(user)}
                        className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-xs text-white focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${
                          user.role === 'admin'
                            ? 'bg-noir-tertiary text-text-secondary hover:bg-noir-secondary focus:ring-border'
                            : 'bg-accent-cyan hover:bg-accent-cyan/80 focus:ring-accent-cyan'
                        }`}
                        disabled={updateUserRole.isPending}
                        aria-label={
                          user.role === 'admin'
                            ? `Demote ${user.username} to user role`
                            : `Promote ${user.username} to admin role`
                        }
                      >
                        {user.role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-text-secondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-text-primary">No users found</h3>
                <p className="mt-1 text-sm text-text-secondary">
                  No users are registered in the system.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setConfirmDialog(null)
            }
          }}
        >
          <div
            className="bg-noir-secondary border border-border rounded-lg max-w-md w-full p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
          >
            <div className="flex items-start">
              <div className="shrink-0">
                <svg
                  className="h-6 w-6 text-verdict-inconclusive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 id="dialog-title" className="text-lg font-medium text-text-primary">
                  Confirm Role Change
                </h3>
                <div className="mt-2 text-sm text-text-secondary">
                  <p>
                    Are you sure you want to change{' '}
                    <span className="font-semibold">{confirmDialog.user.username}</span>'s role
                    from{' '}
                    <span className="font-semibold">{confirmDialog.user.role}</span> to{' '}
                    <span className="font-semibold">{confirmDialog.newRole}</span>?
                  </p>
                  {confirmDialog.newRole === 'admin' && (
                    <p className="mt-2 text-verdict-inconclusive font-medium">
                      This will grant full administrative privileges to this user.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                className="inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-text-secondary bg-noir-tertiary hover:bg-noir-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-border"
                disabled={updateUserRole.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRoleChange}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-verdict-rejected hover:bg-verdict-rejected/80 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-verdict-rejected"
                disabled={updateUserRole.isPending}
              >
                {updateUserRole.isPending ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
